import { useEffect, useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ShoppingBag, Shirt, PackageCheck, Package, CheckCircle2, Undo2, Search, Loader2, Phone, Download, History, User, MessageCircle,
} from "lucide-react";
import { listSales, markSaleDelivered, undoSaleDelivered, type SaleRow } from "@/lib/api/sales.functions";
import { paymentMethodLabel } from "@/lib/payment-methods";
import { brToday } from "@/lib/tz";
import { SaleWhatsappBillingModal, type SaleBillingInput } from "@/components/sales/SaleWhatsappBillingModal";

export const Route = createFileRoute("/_authenticated/m/vendas")({
  component: VendasPage,
});

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n || 0);
const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";
const fmtDateTime = (s: string | null) =>
  s ? new Date(s).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";

export function VendasPage() {
  const listFn = useServerFn(listSales);
  const markFn = useServerFn(markSaleDelivered);
  const undoFn = useServerFn(undoSaleDelivered);

  const [loading, setLoading] = useState(true);
  const [sales, setSales] = useState<SaleRow[]>([]);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | "uniform" | "product">("all");
  const [deliveryFilter, setDeliveryFilter] = useState<"all" | "pending" | "delivered">("pending");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState<"all" | "aguardando" | "pago" | "cancelado">("all");
  const [sellerFilter, setSellerFilter] = useState<string>("all");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [saleFrom, setSaleFrom] = useState("");
  const [saleTo, setSaleTo] = useState("");
  const [deliveryFrom, setDeliveryFrom] = useState("");
  const [deliveryTo, setDeliveryTo] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [deliverModal, setDeliverModal] = useState<SaleRow | null>(null);
  const [billing, setBilling] = useState<SaleBillingInput | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await listFn();
      setSales(res.sales ?? []);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro ao carregar vendas");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  const isPaymentPending = (status: string) => status !== "pago" && status !== "cancelado";

  const sellers = useMemo(() => {
    const set = new Set<string>();
    sales.forEach((s) => { if (s.sold_by) set.add(s.sold_by); });
    return Array.from(set).sort();
  }, [sales]);

  const filtered = useMemo(() => {
    const inRange = (iso: string | null, from: string, to: string) => {
      if (!from && !to) return true;
      if (!iso) return false;
      const d = iso.slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    };
    return sales.filter((s) => {
      if (kindFilter !== "all" && s.kind !== kindFilter) return false;
      const delivered = !!s.delivery_date;
      if (deliveryFilter === "pending" && delivered) return false;
      if (deliveryFilter === "delivered" && !delivered) return false;
      if (paymentStatusFilter === "aguardando" && !isPaymentPending(s.status)) return false;
      if (paymentStatusFilter === "pago" && s.status !== "pago") return false;
      if (paymentStatusFilter === "cancelado" && s.status !== "cancelado") return false;
      if (sellerFilter !== "all" && s.sold_by !== sellerFilter) return false;
      if (!inRange(s.created_at, saleFrom, saleTo)) return false;
      if (deliveryFrom || deliveryTo) {
        const target = s.delivery_date ?? s.expected_delivery_date;
        if (!inRange(target, deliveryFrom, deliveryTo)) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        if (!(s.item_name.toLowerCase().includes(q) || s.buyer.toLowerCase().includes(q))) return false;
      }
      return true;
    });
  }, [sales, kindFilter, deliveryFilter, paymentStatusFilter, sellerFilter, search, saleFrom, saleTo, deliveryFrom, deliveryTo]);

  const stats = useMemo(() => {
    const pending = sales.filter((s) => !s.delivery_date);
    const total = sales.reduce((a, s) => a + s.net, 0);
    return {
      total: sales.length,
      pending: pending.length,
      delivered: sales.length - pending.length,
      revenue: total,
    };
  }, [sales]);

  const monthlyHistory = useMemo(() => {
    const map = new Map<string, { key: string; label: string; count: number; total: number }>();
    sales.forEach((s) => {
      if (!s.created_at) return;
      const d = new Date(s.created_at);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
      const entry = map.get(key) ?? { key, label, count: 0, total: 0 };
      entry.count += 1;
      entry.total += s.net;
      map.set(key, entry);
    });
    return Array.from(map.values()).sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [sales]);

  return (
    <div className="space-y-6">
      <div className="rounded-2xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border p-6 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="rounded-xl bg-primary/15 p-3 text-primary"><ShoppingBag className="h-7 w-7" /></div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
              Vendas e Controle de Entregas
            </h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Uniformes e mercadorias vendidos na Loja. Acompanhe comprador, forma de pagamento e dê baixa na entrega (só disponível após o pagamento).
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)}>
            <History className="h-4 w-4 mr-1" /> Histórico de vendas mensal
          </Button>
          <Button variant="outline" size="sm" onClick={() => setExportOpen(true)}>
            <Download className="h-4 w-4 mr-1" /> Exportar CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={ShoppingBag} label="Vendas totais" value={String(stats.total)} />
        <StatCard icon={Package} label="Aguardando entrega" value={String(stats.pending)} tone="warning" />
        <StatCard icon={PackageCheck} label="Entregues" value={String(stats.delivered)} tone="success" />
        <StatCard icon={ShoppingBag} label="Receita bruta" value={fmtMoney(stats.revenue)} />
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Buscar por item ou comprador..." className="pl-8"
                value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <Select value={kindFilter} onValueChange={(v) => setKindFilter(v as any)}>
              <SelectTrigger className="w-full md:w-52"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os itens</SelectItem>
                <SelectItem value="uniform">Uniformes</SelectItem>
                <SelectItem value="product">Mercadorias</SelectItem>
              </SelectContent>
            </Select>
            <Select value={deliveryFilter} onValueChange={(v) => setDeliveryFilter(v as any)}>
              <SelectTrigger className="w-full md:w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as entregas</SelectItem>
                <SelectItem value="pending">Aguardando entrega</SelectItem>
                <SelectItem value="delivered">Já entregues</SelectItem>
              </SelectContent>
            </Select>
            <Select value={paymentStatusFilter} onValueChange={(v) => setPaymentStatusFilter(v as any)}>
              <SelectTrigger className="w-full md:w-56"><SelectValue placeholder="Situação do pagamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as situações</SelectItem>
                <SelectItem value="aguardando">Aguardando pagamento</SelectItem>
                <SelectItem value="pago">Pago</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sellerFilter} onValueChange={(v) => setSellerFilter(v)}>
              <SelectTrigger className="w-full md:w-56"><SelectValue placeholder="Vendedor" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os vendedores</SelectItem>
                {sellers.map((name) => (
                  <SelectItem key={name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Venda de</Label>
              <Input type="date" value={saleFrom} onChange={(e) => setSaleFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Venda até</Label>
              <Input type="date" value={saleTo} onChange={(e) => setSaleTo(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Entrega de</Label>
              <Input type="date" value={deliveryFrom} onChange={(e) => setDeliveryFrom(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Entrega até</Label>
              <Input type="date" value={deliveryTo} onChange={(e) => setDeliveryTo(e.target.value)} />
            </div>
          </div>
          {(saleFrom || saleTo || deliveryFrom || deliveryTo) && (
            <Button variant="ghost" size="sm" className="h-7"
              onClick={() => { setSaleFrom(""); setSaleTo(""); setDeliveryFrom(""); setDeliveryTo(""); }}>
              Limpar filtros de data
            </Button>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-10 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
          Carregando vendas...
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 flex flex-col items-center text-center gap-3 border-dashed">
          <ShoppingBag className="h-12 w-12 text-muted-foreground/40" />
          <h3 className="font-semibold">Nenhuma venda encontrada</h3>
          <p className="text-sm text-muted-foreground max-w-md">
            Registre vendas em Loja → Uniformes ou Mercadorias.
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((s) => {
            const delivered = !!s.delivery_date;
            const KindIcon = s.kind === "uniform" ? Shirt : ShoppingBag;
            return (
              <Card key={`${s.kind}-${s.id}`} className="overflow-hidden">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0">
                      <div className="rounded-md bg-primary/10 p-2 text-primary shrink-0">
                        <KindIcon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold truncate">{s.item_name}</div>
                        <div className="text-xs text-muted-foreground">
                          {s.kind === "uniform" && s.size ? `Tam. ${s.size} · ` : ""}
                          {s.quantity > 1 ? `${s.quantity}× · ` : ""}
                          {fmtDateTime(s.created_at)}
                        </div>
                      </div>
                    </div>
                    <Badge variant={delivered ? "secondary" : "outline"}
                      className={delivered ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}>
                      {delivered ? "Entregue" : "A entregar"}
                    </Badge>
                  </div>

                  <div className="rounded-md border bg-muted/20 p-2 text-xs space-y-1">
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Comprador</span><span className="font-medium truncate">{s.buyer}</span></div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground flex items-center gap-1"><User className="h-3 w-3" />Vendedor</span>
                      <span className="font-medium truncate">{s.sold_by ?? "—"}</span>
                    </div>
                    {s.buyer_phone && (
                      <div className="flex justify-between gap-2">
                        <span className="text-muted-foreground flex items-center gap-1"><Phone className="h-3 w-3" />Telefone</span>
                        <span className="font-medium">{s.buyer_phone}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Pagamento</span><span className="font-medium">{paymentMethodLabel(s.payment_method)}</span></div>
                    <div className="flex justify-between gap-2">
                      <span className="text-muted-foreground">Status</span>
                      <Badge variant="outline"
                        className={s.status === "pago"
                          ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                          : "bg-amber-50 text-amber-800 border-amber-200"}>
                        {s.status === "pago" ? "Pago" : "Pendente"}
                      </Badge>
                    </div>
                    <div className="flex justify-between gap-2"><span className="text-muted-foreground">Valor</span><span className="font-bold text-primary">{fmtMoney(s.net)}</span></div>
                  </div>

                  <div className="rounded-md border p-2 text-xs">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium flex items-center gap-1">
                        <PackageCheck className="h-3.5 w-3.5" /> Entrega
                      </span>
                      <Badge variant="outline" className="text-[10px]">
                        {s.delivery_type === "futura" ? "Futura" : "Imediata"}
                      </Badge>
                    </div>
                    {delivered ? (
                      <div className="text-muted-foreground">
                        Entregue em <strong className="text-foreground">{fmtDateTime(s.delivery_date)}</strong>
                        {s.delivered_by_name ? <> por <strong className="text-foreground">{s.delivered_by_name}</strong></> : null}
                        {s.receiver_name ? <> para <strong className="text-foreground">{s.receiver_name}</strong></> : null}
                      </div>
                    ) : s.delivery_type === "futura" && s.expected_delivery_date ? (
                      <div className="text-muted-foreground">
                        Prevista para <strong className="text-foreground">{fmtDate(s.expected_delivery_date)}</strong>
                      </div>
                    ) : (
                      <div className="text-muted-foreground">Aguardando baixa</div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    {s.status !== "pago" && s.status !== "cancelado" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-emerald-500 text-emerald-700 hover:bg-emerald-50"
                        onClick={() => setBilling({
                          id: s.id,
                          kind: s.kind,
                          amount: s.net,
                          itemLabel: s.item_name,
                          payerName: s.buyer,
                          phone: s.buyer_phone ?? null,
                          paymentMethod: s.payment_method,
                        })}
                      >
                        <MessageCircle className="h-4 w-4 mr-1" /> Cobrar por WhatsApp
                      </Button>
                    )}
                    {delivered ? (
                      <Button variant="outline" size="sm" className="flex-1"
                        onClick={async () => {
                          if (!confirm("Desfazer baixa de entrega?")) return;
                          try { await undoFn({ data: { kind: s.kind, id: s.id } }); toast.success("Baixa desfeita"); reload(); }
                          catch (e: any) { toast.error(e?.message ?? "Erro"); }
                        }}>
                        <Undo2 className="h-4 w-4 mr-1" /> Desfazer baixa
                      </Button>
                    ) : s.status !== "pago" ? (
                      <Button size="sm" className="flex-1" disabled title="Só é possível dar baixa após o pagamento">
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Aguardando pagamento
                      </Button>
                    ) : (
                      <Button size="sm" className="flex-1" onClick={() => setDeliverModal(s)}>
                        <CheckCircle2 className="h-4 w-4 mr-1" /> Dar baixa na entrega
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <SaleWhatsappBillingModal
        open={!!billing}
        onOpenChange={(v) => { if (!v) setBilling(null); }}
        data={billing}
      />

      <DeliverModal
        sale={deliverModal}
        onOpenChange={(v) => { if (!v) setDeliverModal(null); }}
        onConfirm={async ({ receiver, deliveredBy }) => {
          if (!deliverModal) return;
          try {
            await markFn({ data: { kind: deliverModal.kind, id: deliverModal.id, receiver_name: receiver || null, delivered_by_name: deliveredBy } });
            toast.success("Entrega registrada!");
            setDeliverModal(null);
            reload();
          } catch (e: any) { toast.error(e?.message ?? "Erro"); }
        }}
      />

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="h-5 w-5" /> Exportar CSV</DialogTitle>
            <DialogDescription>Escolha quais itens exportar (aplica os filtros ativos).</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Button variant="outline" onClick={() => { exportSalesCsv(filtered, "all"); setExportOpen(false); }}>
              <Package className="h-4 w-4 mr-2" /> Tudo
            </Button>
            <Button variant="outline" onClick={() => { exportSalesCsv(filtered, "uniform"); setExportOpen(false); }}>
              <Shirt className="h-4 w-4 mr-2" /> Somente uniformes
            </Button>
            <Button variant="outline" onClick={() => { exportSalesCsv(filtered, "product"); setExportOpen(false); }}>
              <ShoppingBag className="h-4 w-4 mr-2" /> Somente mercadorias
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Histórico de vendas mensal</DialogTitle>
            <DialogDescription>Totalização de vendas por mês, do mais recente ao mais antigo.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {monthlyHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">Nenhuma venda registrada ainda.</p>
            ) : (
              monthlyHistory.map((m) => (
                <div key={m.key} className="flex items-center justify-between rounded-md border p-3 text-sm">
                  <div>
                    <div className="font-medium capitalize">{m.label}</div>
                    <div className="text-xs text-muted-foreground">{m.count} venda{m.count !== 1 ? "s" : ""}</div>
                  </div>
                  <div className="font-bold text-primary">{fmtMoney(m.total)}</div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone }: { icon: any; label: string; value: string; tone?: "success" | "warning" }) {
  const toneCls =
    tone === "success" ? "bg-emerald-100 text-emerald-700"
    : tone === "warning" ? "bg-amber-100 text-amber-700"
    : "bg-primary/10 text-primary";
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`rounded-lg p-2 ${toneCls}`}><Icon className="h-5 w-5" /></div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground truncate">{label}</div>
          <div className="text-xl font-bold leading-tight">{value}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function DeliverModal({
  sale, onOpenChange, onConfirm,
}: { sale: SaleRow | null; onOpenChange: (v: boolean) => void; onConfirm: (data: { receiver: string; deliveredBy: string }) => Promise<void> }) {
  const [receiver, setReceiver] = useState("");
  const [deliveredBy, setDeliveredBy] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (sale) {
      setReceiver(sale.buyer ? `${sale.buyer} (preenchimento automático)` : "");
      setDeliveredBy("");
    }
  }, [sale]);

  return (
    <Dialog open={!!sale} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5" /> Dar baixa na entrega</DialogTitle>
          <DialogDescription>{sale?.item_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Quem entregou? <span className="text-destructive">*</span></Label>
            <Input value={deliveredBy} onChange={(e) => setDeliveredBy(e.target.value)} placeholder="Nome do responsável pela entrega" />
          </div>
          <div>
            <Label>Quem recebeu? <span className="text-muted-foreground text-xs">(opcional)</span></Label>
            <Input value={receiver} onChange={(e) => setReceiver(e.target.value)} placeholder="Nome de quem retirou o item" />
          </div>
          <p className="text-xs text-muted-foreground">A data e o horário serão registrados automaticamente.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={async () => {
              if (!deliveredBy.trim()) { toast.error("Informe quem realizou a entrega"); return; }
              setSaving(true);
              try { await onConfirm({ receiver: receiver.trim(), deliveredBy: deliveredBy.trim() }); }
              finally { setSaving(false); }
            }}
            disabled={saving}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirmar entrega
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Agrupa itens idênticos (mesmo item, mesmo tamanho, sem nome/número personalizado)
// e exporta CSV usando ponto-e-vírgula (compatível com Excel BR).
function exportSalesCsv(sales: SaleRow[], kind: "all" | "uniform" | "product" = "all") {
  const source = kind === "all" ? sales : sales.filter((s) => s.kind === kind);
  type Key = string;
  const groups = new Map<Key, { row: SaleRow; qty: number; total: number }>();
  for (const s of source) {
    const personalized = !!(s.shirt_name || s.shirt_number);
    const key: Key = personalized
      ? `single:${s.id}`
      : `${s.kind}|${s.item_name}|${s.size ?? ""}`;
    const g = groups.get(key);
    const qty = s.quantity || 1;
    if (g) { g.qty += qty; g.total += s.net; }
    else groups.set(key, { row: s, qty, total: s.net });
  }
  const header = [
    "Tipo","Item","Tamanho","Quantidade","Nome (uniforme)","Nº (uniforme)",
    "Comprador","Telefone","Pagamento","Status Entrega","Prev. Entrega","Data Venda","Valor Total",
  ];
  const rows: string[][] = [header];
  for (const { row: s, qty, total } of groups.values()) {
    rows.push([
      s.kind === "uniform" ? "Uniforme" : "Mercadoria",
      s.item_name,
      s.size ?? "",
      String(qty),
      s.shirt_name ?? "",
      s.shirt_number ?? "",
      s.buyer,
      s.buyer_phone ?? "",
      s.payment_method,
      s.delivery_date ? "Entregue" : (s.delivery_type === "futura" ? "Futura" : "Pendente"),
      s.expected_delivery_date ?? "",
      new Date(s.created_at).toLocaleString("pt-BR"),
      total.toFixed(2).replace(".", ","),
    ]);
  }
  const csv = rows.map((r) => r.map((c) => {
    const v = String(c ?? "");
    return /[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
  }).join(";")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vendas-${kind}-${brToday()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

